import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import { Alert, LinearProgress, Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { take } from "lodash";
import { useSnackbar } from "notistack";
import type { FC } from "react";
import { useMemo, useState } from "react";
import InfiniteScroll from "../../components/infiniteScroll/InfiniteScroll";
import CreateCallRecordingNoteDialog from "../../customers/callRecordings/CreateCallRecordingNoteDialog";
import { useCallRecordingNoteDraft } from "../../customers/callRecordings/useCallRecordingNoteDraft";
import { useCallRecordingPlayback } from "../../customers/callRecordings/useCallRecordingPlayback";
import type { AddCustomerNoteRequest } from "../../customers/notes/customerNotesApi";
import { addCustomerNote } from "../../customers/notes/customerNotesApi";
import { invalidateCustomerNotesQueries } from "../../customers/notes/useCustomerNotes";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import { useLimit } from "../../misc/useLimit";
import { useAdminFiltersValue } from "../useAdminFilters";
import { AgentCallsList } from "./AgentCallsList";
import { useAgentCallRows } from "./useAgentCallRows";
import { useAgentPhoneCallsAdmin } from "./useAgentPhoneCallsAdmin";

const NoCallsPlaceholder = () => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      alignItems: "center",
      color: "text.secondary",
      marginTop: 1,
    }}
  >
    <PhoneDisabledIcon fontSize="small" />
    <Typography>Brak rozmów dla wybranych filtrów</Typography>
  </Stack>
);

interface SaveNoteVariables {
  readonly customerId: string;
  readonly request: AddCustomerNoteRequest;
}

const AgentCallsAdminPage: FC = () => {
  const { dateFrom, dateTo, agentId } = useAdminFiltersValue("phone-calls");
  const {
    data: calls = [],
    isLoading,
    error,
  } = useAgentPhoneCallsAdmin({
    dateFrom,
    dateTo,
    agentId: agentId ?? undefined,
  });
  const { limit, loadMore } = useLimit();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const rows = useAgentCallRows(calls);

  const { audioRef, playingRecId, loadingRecId, handlePlayToggle } = useCallRecordingPlayback();
  const { generatingNoteRecId, noteDraftRecId, noteDraftText, handleCreateNoteDraft, closeDraft } =
    useCallRecordingNoteDraft();

  const [dialogCustomerId, setDialogCustomerId] = useState<string | null>(null);

  const noteDraftRow = useMemo(() => rows.find((row) => row.id === noteDraftRecId) ?? null, [rows, noteDraftRecId]);

  const saveNoteMutation = useMutation<void, unknown, SaveNoteVariables>({
    mutationFn: ({ customerId, request }) => addCustomerNote(customerId, request),
    onSuccess: async (_, { customerId }) => {
      enqueueSnackbar("Notatka została dodana", { variant: "success" });
      await invalidateCustomerNotesQueries(queryClient, customerId);
    },
  });

  const handleOpenNoteDraft = async (id: string) => {
    const row = rows.find((r) => r.id === id);
    setDialogCustomerId(row?.clientId ?? null);
    await handleCreateNoteDraft(id);
  };

  const handleSaveNoteDraft = async (editedText: string, customerId: string) => {
    await saveNoteMutation.mutateAsync({
      customerId,
      request: {
        text: editedText,
        type: "Telefon",
        category: "PRZEZMORZE",
        date: noteDraftRow ? dayjs(noteDraftRow.startTime).format(ISO_DATE_FORMAT) : undefined,
      },
    });
  };

  const totalMinutes = useMemo(() => Math.round(calls.reduce((sum, call) => sum + call.length, 0) / 60000), [calls]);

  const isNotConfigured = isAxiosError(error) && error.response?.status === 501;

  return (
    <Stack
      spacing={1}
      sx={{
        marginTop: 1,
        height: "100%",
      }}
    >
      {!isLoading && !error && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Suma: {totalMinutes} min
        </Typography>
      )}
      {isLoading && <LinearProgress />}
      {isNotConfigured && <Alert severity="info">Funkcja rejestru rozmów nie jest jeszcze skonfigurowana.</Alert>}
      {error && !isNotConfigured && <Alert severity="error">Nie udało się pobrać listy rozmów.</Alert>}
      {!error && !isLoading && rows.length === 0 && <NoCallsPlaceholder />}
      {!error && !isLoading && rows.length > 0 && (
        <InfiniteScroll
          dataLength={limit < rows.length ? limit : rows.length}
          fetchData={loadMore}
          hasMore={limit < rows.length}
        >
          <AgentCallsList
            rows={take(rows, limit)}
            playingRecId={playingRecId}
            loadingRecId={loadingRecId}
            generatingNoteRecId={generatingNoteRecId}
            onPlayToggle={handlePlayToggle}
            onCreateNoteDraft={handleOpenNoteDraft}
          />
        </InfiniteScroll>
      )}
      <audio ref={audioRef} hidden />
      {noteDraftRecId !== null && (
        <CreateCallRecordingNoteDialog
          draftText={noteDraftText}
          customerId={dialogCustomerId}
          readOnlyCustomer={false}
          onCustomerChange={setDialogCustomerId}
          onClose={closeDraft}
          onSave={handleSaveNoteDraft}
        />
      )}
    </Stack>
  );
};

export default AgentCallsAdminPage;
